import { useCallback, useEffect, useState } from "react";
import { levelsColor } from "../filterSettings";
import { useLanguage } from "../../../stores/LanguageStore";
import FilterItem from "../FilterItem";
import FilterItemAdder from "../FilterItemAdder";
import FilterGroupAdder from "../FilterGroupAdder";
import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import { IconCheck, IconRotate } from "@tabler/icons-react";
import FilterModeSelector from "../FilterModeSelector";
import FilterBlockChild from "../FilterBlockChild";
import FilterSaver from "../FilterSaver";
import FilterItemMobile from "./FilterItemMobile";
import FilterBlockChildMobile from "./FilterBlockChildMobile";

export const conditionWidth = 360;

let newId = 0;

function countObjectsWithValue(arr) {
  let count = 0;
  arr.forEach((obj) => {
    if (Object.prototype.hasOwnProperty.call(obj, "value")) {
      count++;
    }
    if (Object.prototype.hasOwnProperty.call(obj, "values")) {
      count += countObjectsWithValue(obj.values);
    }
  });
  return count;
}

const FilterBlockRootMobile = ({
  filter,
  submitFilter,
  fields,
  id,
  maxItems,
  maxHeight = 300,
  disableButtons,
  getFieldOptionName,
  isClient,
  onSaved,
  pageName,
  buttonLoading,
  disableEdit,
  message,
}) => {
  const lang = useLanguage((s) => s.language);

  const [_mode, _setMode] = useState(filter?.mode || "and");

  const onModeChange = (v) => {
    _setMode(v);
  };

  const [items, setItems] = useState(
    filter?.values.map((x) => ({ ...x, id: newId++ })) || []
  );

  const addItem = () => {
    const newItem = {
      field_name: fields[0].options[0].id.toString(),
      type:
        fields[0].options[0]?.type === "number"
          ? isClient
            ? "="
            : "equals"
          : "contains",
      value: "",
      id: newId++,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const addGroup = () => {
    const newItem = {
      id: newId++,
      mode: "and",
      values: [
        {
          field_name: fields[0].options[0].id.toString(),
          type: fields[0].options[0]?.type === "number" ? "=" : "contains",
          value: "",
          id: newId++,
        },
      ],
    };
    setItems((prev) => [...prev, newItem]);
  };

  const deleteItem = (id) => {
    setItems((prev) => {
      let newItems = prev.filter((x) => x.id !== id);

      return newItems;
    });
  };

  const deleteGroup = (id) => {
    setItems((prev) => {
      let newItems = prev.filter((x) => x.id !== id);

      return newItems;
    });
  };

  const handleItemChange = useCallback(({ id, field_name, type, value }) => {
    setItems((prev) => {
      let newItems = [...prev];
      let index = newItems.findIndex((x) => x.id === id);

      newItems[index] = {
        field_name: field_name,
        type: type,
        value: value,
        id: id,
      };

      return newItems;
    });
  }, []);

  const handleGroupChange = useCallback((id, newValues) => {
    setItems((prev) => {
      let newItems = [...prev];
      let index = newItems.findIndex((x) => x.id === id);
      newItems[index] = { ...newValues, id: id };

      return newItems;
    });
  }, []);

  const handleReset = () => {
    _setMode("and");
    setItems([
      {
        field_name: fields[0].options[0].id.toString(),
        type: fields[0].options[0]?.type === "number" ? "=" : "contains",
        value: "",
        id: 1,
      },
    ]);
    newId = 1;
  };

  const handleFilter = (e) => {
    e.preventDefault();
    submitFilter({ mode: _mode, values: items });
    e.stopPropagation();
  };

  const [itemNumbers, setItemNumbers] = useState(countObjectsWithValue(items));

  useEffect(() => {
    setItemNumbers(countObjectsWithValue(items));
  }, [items]);

  useEffect(() => {
    setItems(filter?.values.map((x) => ({ ...x, id: newId++ })) || []);
    _setMode(filter?.mode || "and");
  }, [filter]);

  return (
    <Paper h={"100%"} w={"100%"} component={Stack}>
      {message && (
        <Text size="sm" c={"red"} mb={-8}>
          {message}
        </Text>
      )}
      <Stack
        pos="relative"
        component="form"
        id={"main-filter-form" + id}
        onSubmit={handleFilter}
        flex={"1 1 auto"}
      >
        <Stack pos="relative" gap={"xl"}>
          {items.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 14,
                left: 11,
                width: 0,
                height: "calc(100% + 14px)",
                borderRight: "1px solid",
                borderColor: levelsColor[0],
              }}
            />
          )}
          {items.map((x, i) =>
            x?.values ? (
              <FilterBlockChildMobile
                key={x.id}
                fields={fields}
                getFieldOptionName={getFieldOptionName}
                handleChange={(newItems) => handleGroupChange(x.id, newItems)}
                handleDeleteGroup={() => deleteGroup(x.id)}
                values={x.values}
                mode={x.mode}
                isLast={i === items.length - 1}
                parentMode={_mode}
                itemNumbers={itemNumbers}
                maxItems={maxItems}
                disableEdit={disableEdit}
                filterItems={items}
                pageName={pageName}
              />
            ) : (
              <FilterItemMobile
                item={x}
                key={x.id}
                lineColor={levelsColor[0]}
                isLast={i === items.length - 1}
                parentMode={_mode}
                handleDelete={() => deleteItem(x.id)}
                handleChange={(newFilter) =>
                  handleItemChange({
                    ...newFilter,
                    id: x.id,
                  })
                }
                fields={fields}
                isClient={isClient}
                getFieldOptionName={getFieldOptionName}
                filterItems={items}
                levelItems={items}
                pageName={pageName}
                _mode={_mode}
                onModeChange={onModeChange}
                disableEdit={disableEdit}
                level={0}
              />
            )
          )}

          {items.length === 0 ? (
            <Group>
              <Button onClick={addItem} variant="outline" flex={1}>
                {lang.table.toolbar.filter.conditions.add_condition_tooltip}
              </Button>
            </Group>
          ) : (
            <Stack ml={"sm"} pl={25} pos="relative">
              <div
                style={{
                  position: "absolute",
                  top: "calc(50% - 1px)",
                  left: 0,
                  width: 23,
                  border: 0,
                  borderTop: "1px dashed",
                  borderColor: levelsColor[0],
                }}
              />
              <Group gap={"lg"}>
                <FilterItemAdder
                  disabled={itemNumbers >= maxItems || disableEdit}
                  onClick={addItem}
                  color={levelsColor[0]}
                />
                <FilterGroupAdder
                  disabled={itemNumbers >= maxItems || disableEdit}
                  onClick={addGroup}
                  color={levelsColor[0]}
                />
              </Group>
            </Stack>
          )}
        </Stack>
      </Stack>

      {!disableButtons && (
        <Group mt={"auto"} wrap="nowrap" pt={"xs"} justify="space-between">
          <FilterSaver
            onSaved={onSaved}
            filter={{ mode: _mode, values: items }}
            pageName={pageName}
            isMobile
          />

          <Button
            leftSection={<IconCheck size={16} />}
            type="submit"
            form={"main-filter-form" + id}
            id="filter-apply-button"
            loading={buttonLoading}
            disabled={disableEdit}
          >
            {lang.table.toolbar.filter.submit_button_label}
          </Button>
        </Group>
      )}
    </Paper>
  );
};

export default FilterBlockRootMobile;
